import { createSurveyStyles } from '@sm/webassets/SurveyTheme/context';
import { CITY_LIGHTS_SURVEY_TITLE_WRAPPER_BACKGROUND_COLOR, isCityLights, isV2Theme } from '~app/helpers/themeLayout';
import { SurveyTitleHorizontalAlignment } from '~lib/generatedGqlTypes';

const calculateSurveyTitleWidth = (v2Theme: boolean, align: SurveyTitleHorizontalAlignment): string | null => {
  if (align === 'CENTER') {
    return null;
  }
  return v2Theme ? '95%' : '100%';
};

const useSurveyStyles = createSurveyStyles<
  'surveyTitle' | 'surveyTitleWrapper' | 'surveyTitleOuterWrapper',
  {
    align: SurveyTitleHorizontalAlignment;
    logoVerticalAlignment: 'BASELINE' | 'BOTTOM' | undefined;
    showTitleText: boolean;
  }
>(theme => ({
  surveyTitle: {
    color: theme?.titleColor ?? '#00bf6f',
    lineHeight: 1.25,
    paddingBottom: 24,
    margin: 0,
    width: ({ align }) => calculateSurveyTitleWidth(isV2Theme(theme.version), align),
    // use `start` to support RTL (as 'LEFT' means `right` in RTL languages)
    textAlign: ({ align }) => (align === 'CENTER' ? 'center' : 'start'),
    ...theme?.surveyTitle,
    // reset font weight changed by the theme above
    '& strong': {
      fontWeight: 'bold !important',
    },
    ...(isV2Theme(theme.version) && {
      WebkitFontSmoothing: 'antialiased',
      paddingBottom: ({ logoVerticalAlignment }) => (logoVerticalAlignment === 'BASELINE' ? 0 : 16),
      paddingLeft: ({ logoVerticalAlignment }) => (logoVerticalAlignment === 'BASELINE' ? 16 : 0),
      ...(isCityLights(theme.surveyTitle) && {
        // need to prevent the background image from applying to the survey title background
        // since it should apply to the whole top section
        backgroundImage: null,
        // setting survey title background color should have no effect for this theme
        backgroundColor: null,
      }),
    }),
    [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
      ...(isV2Theme(theme.version) && {
        paddingLeft: ({ logoVerticalAlignment, align }) =>
          logoVerticalAlignment === 'BASELINE' && align === 'CENTER' ? 0 : null,
      }),
    },
  },
  surveyTitleWrapper: ({ logoVerticalAlignment, align, showTitleText }) => ({
    ...(isV2Theme(theme.version) && {
      paddingTop: showTitleText || logoVerticalAlignment === 'BASELINE' || logoVerticalAlignment === 'BOTTOM' ? 16 : 0,

      ...(logoVerticalAlignment === 'BASELINE' && {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        ...(align === 'CENTER' && {
          justifyContent: 'center',
          [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
            flexDirection: 'column',
            gap: 16,
          },
        }),
      }),

      ...((logoVerticalAlignment === 'BASELINE' || logoVerticalAlignment === 'BOTTOM') && {
        paddingBottom: 16,
      }),

      ...(isCityLights(theme.surveyTitle) && {
        backgroundColor: CITY_LIGHTS_SURVEY_TITLE_WRAPPER_BACKGROUND_COLOR,
        marginLeft: -40,
        marginRight: -40,
        paddingLeft: 40,
        paddingRight: 40,
      }),
    }),
  }),
  surveyTitleOuterWrapper: ({ showTitleText }) => ({
    ...(isCityLights(theme.surveyTitle) &&
      showTitleText && {
        paddingTop: 128,
      }),
  }),
}));

export default useSurveyStyles;
